import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingBag, Plus } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { productImages } from '@/lib/images';
import ProductImage from '@/components/ProductImage';
import { generateSlug } from '@/lib/utils';

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  category?: string;
  description: string;
  descriptionEn?: string;
  image: string;
  region?: string;
  year?: number;
  volume?: string;
  alcohol?: string;
  weight?: string;
  stock?: number;
  isFeatured?: boolean;
  minimal?: boolean;
  inStoreOnly?: boolean;
}

const getThumbUrl = (url: string) => {
  if (!url) return url;
  if (url.includes('_thumb.webp')) return url;
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return url;
  return url.substring(0, lastDotIndex) + '_thumb.webp';
};

const ProductCard = ({
  id,
  name,
  price,
  category = 'inne',
  description,
  descriptionEn,
  image,
  region,
  year,
  volume,
  weight,
  stock,
  salePrice,
  isFeatured,
  minimal = false,
  inStoreOnly = false,
}: ProductCardProps & { salePrice?: number | null }) => {
  const { t, language } = useLanguage();
  const { addToCart } = useCart();

  // Get actual image URL from image key
  const imageUrl = productImages[image] || image;

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const isOutOfStock = stock !== undefined && stock <= 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;
    setIsAddingToCart(true);
    addToCart({ 
      id, 
      name, 
      price: salePrice ?? price, 
      originalPrice: salePrice ? price : undefined,
      image: imageUrl, 
      maxStock: stock,
      inStoreOnly
    });
    setTimeout(() => setIsAddingToCart(false), 600);
  };

  const productUrl = `/produkty/${category}/${id}/${generateSlug(name)}`;

  return (
    <Link to={productUrl} className="flex flex-col h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="card-product group cursor-pointer flex flex-col h-full bg-white rounded-[28px] overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-black/[0.03] transition-all duration-300"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#fcfaf8]">
          <ProductImage
            src={getThumbUrl(imageUrl)}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />

          {/* Quick add button */}
          {!isOutOfStock && (
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              className="absolute bottom-4 right-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-50"
            >
              {isAddingToCart ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }} className="h-5 w-5 border-2 border-primary-foreground border-t-transparent rounded-full" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
            </motion.button>
          )}

          {/* Out of stock overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
              <span className="rounded-full bg-black/70 px-4 py-2 text-sm font-bold text-white uppercase tracking-wider">
                {language === 'PL' ? 'Niedostępny' : 'Out of stock'}
              </span>
            </div>
          )}

          {/* Badges */}
          <div className="absolute left-4 top-4 flex flex-col gap-2">
            {salePrice && (
              <span className="rounded-full bg-[#8b2b3a] px-3 py-1.5 text-[11px] font-bold text-white uppercase tracking-wider backdrop-blur-sm shadow-sm">
                -{Math.round(((price - salePrice) / price) * 100)}% {t('saleBadge')}
              </span>
            )}
            {isFeatured && (
              <span className="flex items-center gap-1.5 rounded-full bg-[#C5A059] px-3 py-1.5 text-[11px] font-bold text-white uppercase tracking-wider shadow-sm backdrop-blur-sm">
                <span className="text-[12px] leading-none">★</span> {t('bestsellerBadge')}
              </span>
            )}
            {region && !minimal && (
              <span className="rounded-full bg-card/90 px-3 py-1 text-xs font-medium backdrop-blur-sm">
                {region}
              </span>
            )}
            {year && !minimal && (
              <span className="rounded-full bg-primary/90 px-3 py-1 text-xs font-medium text-primary-foreground backdrop-blur-sm">
                {year}
              </span>
            )}
          </div>

          {/* Low stock indicator */}
          {stock !== undefined && stock > 0 && stock <= 5 && (
            <div className="absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full bg-red-600/90 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              {t('lastItems')}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="mb-2 font-display text-lg font-semibold text-foreground line-clamp-3 leading-snug">
            {name}
          </h3>

          <p className="mb-4 text-sm text-muted-foreground line-clamp-2">
            {language === 'EN' && descriptionEn ? descriptionEn : description}
          </p>

          {/* Meta info */}
          {!minimal && (volume || weight) && (
            <div className="mb-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {volume && <span className="rounded bg-muted px-2 py-1">{volume}</span>}
              {weight && <span className="rounded bg-muted px-2 py-1">{weight}</span>}
            </div>
          )}

          {/* Price and button */}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-auto pt-4">
            {salePrice ? (
              <div className="flex flex-col">
                <span className="font-display text-xl font-bold text-red-600 whitespace-nowrap">
                  {salePrice.toFixed(2)} zł
                </span>
                <span className="text-xs text-muted-foreground line-through">
                  {price.toFixed(2)} zł
                </span>
              </div>
            ) : (
              <span className="font-display text-xl font-bold text-primary whitespace-nowrap">
                {price} zł
              </span>
            )}

            {isOutOfStock ? (
              <span className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                {language === 'PL' ? 'Niedostępny' : 'Sold out'}
              </span>
            ) : (
              <div className="flex flex-col items-end gap-1">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={isAddingToCart}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold tracking-wide transition-all shadow-sm hover:shadow-md whitespace-nowrap disabled:opacity-50 ${
                    inStoreOnly 
                      ? 'bg-[#C5A059] hover:bg-[#b08d4f] text-white' 
                      : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  }`}
                >
                  {isAddingToCart ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }} className="h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <ShoppingBag className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="hidden min-[400px]:inline">
                    {inStoreOnly 
                      ? (language === 'PL' ? 'Do koszyka (odbiór)' : 'Add (pickup)') 
                      : t('addToCart')}
                  </span>
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default ProductCard;
